import React, { ReactNode, useState, useEffect, useRef } from 'react'
import { CardCustomizationSettings, CardFieldSettings, type FontSize } from '@/shared/hooks/useCardCustomization'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import OverflowMenu from '@/shared/components/ui/OverflowMenu'

export interface CardField {
  key: string
  label: string
  value: string | ReactNode
  settings?: CardFieldSettings
}

interface CustomizableCardProps {
  // Dados básicos
  icon: ReactNode
  title: string
  subtitle?: string
  badge?: {
    label: React.ReactNode
    variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
    icon?: React.ReactNode
  }
  media?: ReactNode
  fields: CardField[]
  
  // Ações
  actions?: Array<{
    label: string
    icon?: ReactNode
    onClick: () => void
    variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
    hidden?: boolean
  }>
  actionsNode?: ReactNode
  footer?: ReactNode
  onClick?: () => void
  
  // Seleção
  selectable?: boolean
  isSelected?: boolean
  onToggleSelection?: () => void
  
  // Customização
  customization: CardCustomizationSettings
  sizeClasses: {
    padding: string
    gap: string
    iconSize: string
    iconPadding: string
  }
  getFontSizeClass: (size: FontSize) => string
  getFieldClasses: (fieldKey: string) => string
}

export function CustomizableCard({
  icon,
  title,
  subtitle,
  badge,
  media,
  fields,
  actions = [],
  actionsNode,
  footer,
  onClick,
  selectable = false,
  isSelected = false,
  onToggleSelection,
  customization,
  sizeClasses,
  getFontSizeClass,
  getFieldClasses
}: CustomizableCardProps) {
  const { t } = useTranslation()
  const badgeColors = {
    primary: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
    success: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
    warning: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
    danger: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
    info: 'bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300'
  }

  const actionColors = {
    primary: 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20',
    danger: 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
  }

  const visibleActions = actions.filter(action => !action.hidden)
  const visibleFields = fields.filter(f => customization.fields[f.key]?.visible !== false)
    .sort((a, b) => {
      const orderA = customization.fields[a.key]?.order ?? 999
      const orderB = customization.fields[b.key]?.order ?? 999
      return orderA - orderB
    })

  const [badgeAnimating, setBadgeAnimating] = useState(false)
  const badgeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    return () => {
      if (badgeTimer.current) clearTimeout(badgeTimer.current)
    }
  }, [])

  // Template Compact (padrão original)
  if (customization.template === 'compact') {
    return (
      <div 
        aria-selected={isSelected}
        className={`group relative bg-white dark:bg-slate-800 rounded-lg border ${
          isSelected 
            ? 'border-emerald-500 dark:border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' 
            : 'border-slate-200 dark:border-slate-700 hover:border-emerald-500 dark:hover:border-emerald-500'
        } ${sizeClasses.padding} hover:shadow-lg transition-all ${
          onClick ? 'cursor-pointer' : ''
        }`}
        onClick={onClick}
      >
        {/* Actions header bar (when actionsNode exists, render it in a dedicated section at top) */}
        {actionsNode && (
          <div className="absolute top-0 right-3">
            {actionsNode}
          </div>
        )}

        {/* Overflow menu: reúne seleção e ações em um menu compacto (only when no actionsNode) */}
        {!actionsNode && (selectable || visibleActions.length > 0) && (
          <div className="absolute top-3 right-3">
            <OverflowMenu
              items={[
                ...(selectable ? [{
                  label: t('list.selectItemGeneric') || 'Marcar',
                  checkable: true,
                  checked: isSelected,
                  onToggle: () => onToggleSelection?.()
                }] : []),
                ...visibleActions.map(action => ({
                  label: action.label,
                  icon: action.icon,
                  onClick: action.onClick,
                  danger: action.variant === 'danger'
                }))
              ]}
            />
          </div>
        )}
        
        {/* Selected badge (top-left) - clickable to unselect */}
        {isSelected && (
          <button
            type="button"
            aria-pressed="true"
            aria-label={t('list.unselectItem') || 'Desmarcar'}
            onClick={(e) => {
              e.stopPropagation()
              if (badgeAnimating) return
              setBadgeAnimating(true)
              badgeTimer.current = setTimeout(() => {
                onToggleSelection?.()
                setBadgeAnimating(false)
              }, 160)
            }}
            className={`absolute -top-2 left-3 w-6 h-6 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-lg border-2 border-white dark:border-slate-800 transition transform duration-150 ${badgeAnimating ? 'scale-90 opacity-60' : 'scale-100 opacity-100'}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" className="w-4 h-4" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 12l4 4L19 6" />
            </svg>
          </button>
        )}
        <div className="flex items-start justify-between mb-">
          <div className={selectable ? 'flex-1 pr-8' : 'flex-1'}>
            <div className="flex items-center gap-1 mb-1">
              <div className={`${sizeClasses.iconPadding} bg-emerald-100 dark:bg-emerald-900/30 rounded-lg`}>
                <div className={sizeClasses.iconSize}>
                  {icon}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`${getFontSizeClass(customization.titleFontSize)} font-semibold text-slate-900 dark:text-white truncate leading-tight`}>
                  {title}
                </h3>
                <div className="flex items-center">
                  {subtitle && (
                    <p className={`${getFontSizeClass(customization.subtitleFontSize)} text-slate-500 dark:text-slate-400 font-mono truncate`}>
                      {subtitle}
                    </p>
                  )}
                </div>
              </div>
              {badge && (
                <span className={`absolute right-4 top-12 shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${badgeColors[badge.variant || 'primary']}`}>
                  {badge.icon}
                  {badge.label}
                </span>
              )}
            </div>

            {visibleFields.length > 0 && (
              <div className={`space-y-1 mt-3`}>
                {visibleFields.map((field) => (
                  <div key={field.key} className="flex items-center gap-2">
                    <span className="text-slate-500 dark:text-slate-400 text-sm">{field.label}:</span>
                    <span className={`text-slate-700 dark:text-slate-300 ${getFieldClasses(field.key)}`}>
                      {field.value}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {footer && (
              <div className="mt-3">
                {footer}
              </div>
            )}
          </div>
        </div>

        {/* Ações agora estão no OverflowMenu no topo direito */}
      </div>
    )
  }

  // Template Image Focus (ícone grande centralizado)
  if (customization.template === 'image-focus') {
    return (
      <div 
        aria-selected={isSelected}
        className={`group relative bg-white dark:bg-slate-800 rounded-lg border ${
          isSelected 
            ? 'border-emerald-500 dark:border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' 
            : 'border-slate-200 dark:border-slate-700 hover:border-emerald-500 dark:hover:border-emerald-500'
        } overflow-hidden hover:shadow-lg transition-all ${
          onClick ? 'cursor-pointer' : ''
        }`}
        onClick={onClick}
      >
        {/* Selected badge (top-left) - clickable to unselect */}
        {isSelected && (
          <button
            type="button"
            aria-pressed="true"
            aria-label={t('list.unselectItem') || 'Desmarcar'}
            onClick={(e) => {
              e.stopPropagation()
              if (badgeAnimating) return
              setBadgeAnimating(true)
              badgeTimer.current = setTimeout(() => {
                onToggleSelection?.()
                setBadgeAnimating(false)
              }, 160)
            }}
            className={`absolute -top-3 left-3 w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-lg border-2 border-white dark:border-slate-800 transition transform duration-150 ${badgeAnimating ? 'scale-90 opacity-60' : 'scale-100 opacity-100'}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" className="w-4 h-4" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 12l4 4L19 6" />
            </svg>
          </button>
        )}
        {/* Ícone grande centralizado */}
        <div className="relative aspect-[16/10] overflow-hidden bg-slate-100 dark:bg-slate-900">
          {media ? (
            media
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-emerald-50 to-sky-100 dark:from-emerald-900/20 dark:to-sky-900/20 flex items-center justify-center">
              <div className="p-4 bg-white/90 dark:bg-slate-800/90 rounded-2xl shadow-lg">
                <div className="w-16 h-16 text-emerald-600 dark:text-emerald-400">
                  {icon}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Conteúdo */}
        <div className={customization.size === 'large' ? 'p-5' : 'p-4'}>
          <div className="mb-2 flex min-h-8 items-start justify-between gap-2">
            <div className="min-w-0 flex-1 text-left">
              <div className="flex min-w-0 items-center gap-1.5">
                <h3 className={`${getFontSizeClass(customization.titleFontSize)} truncate font-bold text-slate-900 dark:text-white`}>
                  {title}
                </h3>
                {badge && (
                  <span className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${badgeColors[badge.variant || 'primary']}`}>
                    {badge.icon}
                    {badge.label}
                  </span>
                )}
              </div>
              {subtitle && (
                <p className={`${getFontSizeClass(customization.subtitleFontSize)} truncate text-slate-500 dark:text-slate-400 font-mono`}>
                  {subtitle}
                </p>
              )}
            </div>

            {(actionsNode || selectable || visibleActions.length > 0) && (
              <div className="flex shrink-0 items-center justify-end gap-1.5">
                {actionsNode}
                {(selectable || visibleActions.length > 0) && (
                  <OverflowMenu
                    items={[
                      ...(selectable ? [{
                        label: t('list.selectItemGeneric') || 'Marcar',
                        checkable: true,
                        checked: isSelected,
                        onToggle: () => onToggleSelection?.()
                      }] : []),
                      ...visibleActions.map(action => ({
                        label: action.label,
                        icon: action.icon,
                        onClick: action.onClick,
                        danger: action.variant === 'danger'
                      }))
                    ]}
                  />
                )}
              </div>
            )}
          </div>

          {visibleFields.length > 0 && (
            <div className="space-y-1.5 pt-2 border-t border-slate-200 dark:border-slate-700">
              {visibleFields.map((field) => (
                <div key={field.key} className="flex justify-between items-center">
                  <span className="text-slate-500 dark:text-slate-400 text-sm">{field.label}</span>
                  <span className={`text-slate-700 dark:text-slate-300 ${getFieldClasses(field.key)}`}>
                    {field.value}
                  </span>
                </div>
              ))}
            </div>
          )}

          {footer && (
            <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700">
              {footer}
            </div>
          )}

          {/* Ações agora estão no OverflowMenu no topo direito */}
        </div>
      </div>
    )
  }

  // Template Details Rich (mais informações, layout compacto)
  if (customization.template === 'details-rich') {
    return (
      <div 
        aria-selected={isSelected}
        className={`group relative bg-white dark:bg-slate-800 rounded-lg border ${
          isSelected 
            ? 'border-emerald-500 dark:border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' 
            : 'border-slate-200 dark:border-slate-700 hover:border-emerald-500 dark:hover:border-emerald-500'
        } ${sizeClasses.padding} hover:shadow-lg transition-all ${
          onClick ? 'cursor-pointer' : ''
        }`}
        onClick={onClick}
      >
        {/* Actions header bar (when actionsNode exists, render it in a dedicated section at top) */}
        {actionsNode && (
          <div className="px-2 py-1 mb-2">
            {actionsNode}
          </div>
        )}

        {/* Overflow menu: reúne seleção e ações em um menu compacto (only when no actionsNode) */}
        {!actionsNode && (selectable || visibleActions.length > 0) && (
          <div className="absolute top-3 right-3">
            <OverflowMenu
              items={[
                ...(selectable ? [{
                  label: t('list.selectItemGeneric') || 'Marcar',
                  checkable: true,
                  checked: isSelected,
                  onToggle: () => onToggleSelection?.()
                }] : []),
                ...visibleActions.map(action => ({
                  label: action.label,
                  icon: action.icon,
                  onClick: action.onClick,
                  danger: action.variant === 'danger'
                }))
              ]}
            />
          </div>
        )}

        {/* Selected badge (top-left) - clickable to unselect */}
        {isSelected && (
          <button
            type="button"
            aria-pressed="true"
            aria-label={t('list.unselectItem') || 'Desmarcar'}
            onClick={(e) => {
              e.stopPropagation()
              if (badgeAnimating) return
              setBadgeAnimating(true)
              badgeTimer.current = setTimeout(() => {
                onToggleSelection?.()
                setBadgeAnimating(false)
              }, 160)
            }}
            className={`absolute -top-3 left-3 w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center shadow-lg border-2 border-white dark:border-slate-800 transition transform duration-150 ${badgeAnimating ? 'scale-90 opacity-60' : 'scale-100 opacity-100'}`}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" className="w-4 h-4" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 12l4 4L19 6" />
            </svg>
          </button>
        )}

        {/* Header compacto */}
        <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-200 dark:border-slate-700">
          <div className={`${sizeClasses.iconPadding} bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex-shrink-0`}>
            <div className={sizeClasses.iconSize}>
              {icon}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className={`${getFontSizeClass(customization.titleFontSize)} font-bold text-slate-900 dark:text-white truncate`}>
                {title}
              </h3>
              {badge && (
                <span className={`flex-shrink-0 px-2 py-0.5 text-xs font-medium rounded-full ${badgeColors[badge.variant || 'primary']}`}>
                  {badge.label}
                </span>
              )}
            </div>
            {subtitle && (
              <p className={`${getFontSizeClass(customization.subtitleFontSize)} text-slate-500 dark:text-slate-400 font-mono truncate`}>
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Grid de campos (2 colunas) */}
        {visibleFields.length > 0 && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-3">
            {visibleFields.map((field) => (
              <div key={field.key} className="min-w-0">
                <div className="text-xs text-slate-500 dark:text-slate-400 mb-0.5">{field.label}</div>
                <div className={`text-slate-700 dark:text-slate-300 truncate ${getFieldClasses(field.key)}`}>
                  {field.value}
                </div>
              </div>
            ))}
          </div>
        )}

        {footer && (
          <div className="mb-3">
            {footer}
          </div>
        )}

        {/* Ações agora estão no OverflowMenu no topo direito */}
      </div>
    )
  }

  // Template List View
  if (customization.template === 'list-view') {
    return (
      <ListViewTemplate
        icon={icon}
        title={title}
        subtitle={subtitle}
        badge={badge}
        fields={fields}
        actions={actions}
        footer={footer}
        onClick={onClick}
        selectable={selectable}
        isSelected={isSelected}
        onToggleSelection={onToggleSelection}
        customization={customization}
        sizeClasses={sizeClasses}
        getFontSizeClass={getFontSizeClass}
        getFieldClasses={getFieldClasses}
        t={t}
        badgeColors={badgeColors}
      />
    )
  }

  // Fallback (não deve acontecer)
  return null
}
function ListViewTemplate({
  icon,
  title,
  subtitle,
  badge,
  fields,
  footer,
  onClick,
  selectable,
  isSelected,
  onToggleSelection,
  customization,
  sizeClasses,
  getFontSizeClass,
  getFieldClasses,
  actions,
  t,
  badgeColors,
}: CustomizableCardProps & { t: (key: string) => string; badgeColors: Record<string, string> }) {
  const [badgeAnimating, setBadgeAnimating] = useState(false)
  const badgeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => { return () => { if (badgeTimer.current) clearTimeout(badgeTimer.current) } }, [])

  const visibleActions = (actions || []).filter(a => !a.hidden)
  const visibleFields = fields
    .filter(f => customization.fields[f.key]?.visible !== false)
    .sort((a, b) => (customization.fields[a.key]?.order ?? 999) - (customization.fields[b.key]?.order ?? 999))

  return (
    <div
      aria-selected={isSelected}
      className={`group relative flex items-center gap-3 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-4 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors ${
        isSelected ? 'bg-emerald-50 dark:bg-emerald-900/20' : ''
      } ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      {/* Seleção */}
      {selectable && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            if (badgeAnimating) return
            setBadgeAnimating(true)
            badgeTimer.current = setTimeout(() => {
              onToggleSelection?.()
              setBadgeAnimating(false)
            }, 120)
          }}
          aria-pressed={isSelected}
          aria-label={isSelected ? (t('list.unselectItem') || 'Desmarcar') : (t('list.selectItemGeneric') || 'Marcar')}
          className={`shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
            isSelected
              ? 'bg-emerald-600 border-emerald-600'
              : 'border-slate-300 dark:border-slate-600 hover:border-emerald-500'
          } ${badgeAnimating ? 'scale-90' : ''}`}
        >
          {isSelected && (
            <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 12l4 4L19 6" />
            </svg>
          )}
        </button>
      )}

      {/* Ícone pequeno */}
      <div className="shrink-0 p-1.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-md">
        <div className="w-4 h-4 text-emerald-600 dark:text-emerald-400">
          {icon}
        </div>
      </div>

      {/* Título + subtítulo */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`${getFontSizeClass(customization.titleFontSize)} font-semibold text-slate-900 dark:text-white truncate`}>
            {title}
          </span>
          {badge && (
            <span className={`shrink-0 px-1.5 py-0.5 text-xs font-medium rounded-full ${badgeColors[badge.variant || 'primary']}`}>
              {badge.label}
            </span>
          )}
          {subtitle && (
            <span className={`${getFontSizeClass(customization.subtitleFontSize)} text-slate-400 dark:text-slate-500 font-mono truncate`}>
              {subtitle}
            </span>
          )}
        </div>
      </div>

      {/* Campos em linha */}
      {visibleFields.length > 0 && (
        <div className="hidden sm:flex items-center gap-4 shrink-0">
          {visibleFields.slice(0, 3).map(field => (
            <div key={field.key} className="flex items-center gap-1 min-w-0">
              <span className="text-xs text-slate-400 dark:text-slate-500 shrink-0">{field.label}:</span>
              <span className={`text-xs text-slate-700 dark:text-slate-300 truncate max-w-[100px] ${getFieldClasses(field.key)}`}>
                {field.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Footer inline */}
      {footer && <div className="shrink-0">{footer}</div>}

      {/* Ações */}
      {visibleActions.length > 0 && (
        <div className="shrink-0" onClick={e => e.stopPropagation()}>
          <OverflowMenu
            items={visibleActions.map(action => ({
              label: action.label,
              icon: action.icon,
              onClick: action.onClick,
              danger: action.variant === 'danger'
            }))}
          />
        </div>
      )}
    </div>
  )
}
